import { HttpClient } from "@angular/common/http";
import { Injectable, signal, computed, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { UserService } from "@kortobaa-front/authentication";
import { environment } from "src/environments/environment";

export type OrgType = "institute" | "maqraa";

export type TenantMembership = {
  organizationId: number;
  slug: string;
  nameAr: string;
  nameEn?: string;
  type: OrgType;
  branding: unknown;
  role:
    | "super_admin"
    | "tenant_admin"
    /** Operational role under tenant_admin: courses + maqraah CRUD + teacher
     * assignment. NO pricing access. NO admin/role management. */
    | "tenant_manager"
    /** Mobile observer/monitor: enters meetings invisible, listen + record.
     * Server-side enforcement in KMeetCore (B-meet follow-on). */
    | "supervisor"
    | "teacher"
    | "student"
    | "parent";
  isPrimary: boolean;
};

const ACTIVE_KEY = "ActiveTenant";

@Injectable({ providedIn: "root" })
export class TenantContextService {
  private readonly _memberships = signal<TenantMembership[]>([]);
  private readonly _active = signal<TenantMembership | null>(null);
  /** Phase G: 'multi_tenant' (default) or 'single_tenant' (white-label
   * deploy with no SaaS dashboard). Loaded from /configurations on
   * app boot. Drives the super-admin menu/route gates below. */
  private readonly _deploymentMode = signal<"multi_tenant" | "single_tenant">("multi_tenant");

  readonly memberships = this._memberships.asReadonly();
  readonly active = this._active.asReadonly();
  readonly deploymentMode = this._deploymentMode.asReadonly();
  readonly isSingleTenant = computed(() => this._deploymentMode() === "single_tenant");

  /** True only when the deployment is multi-tenant AND the active
   * membership is super_admin. Single-tenant deploys never expose
   * super-admin features. */
  readonly isSuperAdmin = computed(
    () => this._deploymentMode() === "multi_tenant" && this._active()?.role === "super_admin"
  );
  readonly isTenantAdmin = computed(() => this._active()?.role === "tenant_admin");
  readonly isTenantManager = computed(() => this._active()?.role === "tenant_manager");
  readonly isSupervisor = computed(() => this._active()?.role === "supervisor");
  /** True if the active role can perform operational admin actions
   * (courses + maqraah + teacher mgmt). Pricing/role-mgmt screens
   * MUST gate on isTenantAdmin/isSuperAdmin instead. */
  readonly canManageOperations = computed(() => {
    const role = this._active()?.role;
    return role === "super_admin" || role === "tenant_admin" || role === "tenant_manager";
  });
  readonly orgType = computed<OrgType | null>(() => this._active()?.type ?? null);
  readonly isInstitute = computed(() => this.orgType() === "institute");
  readonly isMaqraa = computed(() => this.orgType() === "maqraa");
  readonly organizationId = computed(() => this._active()?.organizationId ?? null);

  private readonly userService = inject(UserService);

  constructor(private http: HttpClient) {}

  async load(): Promise<TenantMembership[]> {
    try {
      const token = this.userService.getToken();
      const memberships = await firstValueFrom(
        this.http.get<TenantMembership[]>(`${environment.meetApiHost}api/me/memberships`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );
      this._memberships.set(memberships ?? []);
      this.restoreActiveOrPickDefault(memberships ?? []);
      return memberships ?? [];
    } catch {
      this._memberships.set([]);
      this._active.set(null);
      return [];
    }
  }

  setActive(m: TenantMembership) {
    this._active.set(m);
    try {
      localStorage.setItem(ACTIVE_KEY, JSON.stringify({ organizationId: m.organizationId }));
    } catch {
      /* ignore */
    }
  }

  /** Phase G: called by the configurations bootstrapper after
   * /configurations resolves so the rest of the app can gate
   * super-admin features. */
  setDeploymentMode(mode: "multi_tenant" | "single_tenant" | string | undefined) {
    this._deploymentMode.set(mode === "single_tenant" ? "single_tenant" : "multi_tenant");
  }

  clear() {
    this._memberships.set([]);
    this._active.set(null);
    try {
      localStorage.removeItem(ACTIVE_KEY);
    } catch {
      /* ignore */
    }
  }

  private restoreActiveOrPickDefault(memberships: TenantMembership[]) {
    if (memberships.length === 0) {
      this._active.set(null);
      return;
    }
    let saved: { organizationId?: number } | null = null;
    try {
      const raw = localStorage.getItem(ACTIVE_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    const matched = saved?.organizationId ? memberships.find((m) => m.organizationId === saved!.organizationId) : null;
    const primary = memberships.find((m) => m.isPrimary) ?? memberships[0];
    this._active.set(matched ?? primary);
  }
}
